/**
 * PreviewPlayer — the in-house template/video player (no native controls).
 * Sound ON by default, play/pause button + seekable time bar inside the frame.
 * Used by the Templates gallery preview and the applied-template preview in
 * the agent chat window.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pause, Play } from 'lucide-react';
import { cn } from '@/v2/utils';

export function PreviewPlayer({ src, poster, portrait, autoPlay }: {
  src?: string;
  poster?: string;
  portrait?: boolean;
  /** Start playing on mount (sound on; falls back to muted if the browser blocks it). */
  autoPlay?: boolean;
}) {
  const { t } = useTranslation('v2');
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!autoPlay) return;
    const v = ref.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => {
      // Autoplay with sound blocked — play muted rather than sit frozen.
      v.muted = true;
      void v.play().catch(() => undefined);
    });
  }, [autoPlay, src]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.muted = false; void v.play().catch(() => undefined); }
    else v.pause();
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = ref.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - rect.left) / rect.width) * v.duration;
  };

  if (!src) {
    return poster
      ? <img src={poster} alt="" draggable={false} onContextMenu={(e) => e.preventDefault()} className="w-full h-full object-cover select-none" />
      : <div className="w-full h-full grid place-items-center text-text-tertiary text-sm">{t('previewPlayer.noPreview')}</div>;
  }

  return (
    <div className={cn('relative w-full h-full group/player', portrait ? 'bg-black' : '')}>
      <video
        ref={ref}
        src={src}
        poster={poster}
        loop
        playsInline
        preload="metadata"
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        className="w-full h-full object-contain select-none"
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (v.duration) setProgress((v.currentTime / v.duration) * 100);
        }}
      />
      {/* play / pause — the only control */}
      <button
        onClick={toggle}
        aria-label={playing ? t('previewPlayer.pause') : t('previewPlayer.play')}
        className={cn(
          'absolute inset-0 m-auto w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl transition-all',
          playing ? 'opacity-0 group-hover/player:opacity-100 scale-90' : 'opacity-100 hover:scale-105'
        )}
      >
        {playing
          ? <Pause className="w-5 h-5 text-black fill-black" />
          : <Play className="w-5 h-5 text-black fill-black ml-0.5" />}
      </button>
      {/* time bar — stays inside the frame */}
      <div
        onClick={seek}
        className="absolute bottom-0 inset-x-0 h-6 flex items-end cursor-pointer px-3 pb-2.5"
      >
        <div className="relative w-full h-1 rounded-full bg-white/25 overflow-hidden">
          <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-accent" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
}
